import React, { createContext, useContext, useState, useEffect } from 'react';
import { AdminTokenStorage, AdminAuthAPI } from '../services/adminApi';

interface AdminUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

interface AdminAuthContextType {
  admin: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

// Roles that are allowed into the admin panel.
const ADMIN_ROLES = ['admin', 'superadmin', 'staff'];

// The real backend doesn't always return the full AdminUser shape (e.g. the
// /admin/login/ response only has { email, is_staff }), so fill in sane
// defaults for whatever fields are missing rather than assuming a `role`
// or `id`/`full_name` field exists.
function normalizeAdminUser(data: any): AdminUser | null {
  if (!data) return null;
  return {
    id: data.id ?? data.user_id ?? data.email,
    email: data.email,
    full_name: data.full_name ?? data.name ?? data.email,
    role: data.role ?? (data.is_staff ? 'admin' : ''),
  };
}

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function restoreSession() {
      const token = AdminTokenStorage.getAccess();
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const data = await AdminAuthAPI.me();
        const user = normalizeAdminUser(data);
        if (user && (ADMIN_ROLES.includes(user.role) || data?.is_staff)) {
          AdminTokenStorage.setUser(user);
          setAdmin(user);
        } else {
          AdminTokenStorage.clear();
        }
      } catch {
        // /auth/me/ may not exist for admin sessions yet — fall back to the
        // admin user cached at login time rather than forcing a logout.
        const cached = AdminTokenStorage.getUser();
        if (cached) {
          setAdmin(cached);
        } else {
          AdminTokenStorage.clear();
        }
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  const login = async (email: string, password: string) => {
    // Real endpoint (POST /admin/login/) returns a flat payload:
    // { access, refresh, is_staff, email } — no nested `user`/`tokens`.
    const data = await AdminAuthAPI.login(email, password);
    if (!data?.access || !data?.refresh) {
      throw new Error('This account does not have admin access.');
    }
    const hasAdminAccess = data?.is_staff || ADMIN_ROLES.includes((data?.role || '').toLowerCase());
    if (!hasAdminAccess) {
      throw new Error('This account does not have admin access.');
    }
    const user = normalizeAdminUser(data)!;
    AdminTokenStorage.setTokens(data.access, data.refresh);
    AdminTokenStorage.setUser(user);
    setAdmin(user);
  };

  const logout = async () => {
    try {
      const refresh = AdminTokenStorage.getRefresh();
      if (refresh) await AdminAuthAPI.logout(refresh);
    } catch {}
    AdminTokenStorage.clear();
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
}
